export interface OsethLineInfo {
  LineCode: string
  LineID: string
  LineDescr: string
  LineDescrEng: string
}

export interface OsethRouteInfo {
  RouteCode: string
  LineCode: string
  RouteDescr: string
  RouteDescrEng: string
  RouteType: string
  RouteDistance: string
}

export interface OsethStopInfo {
  StopCode: string
  StopID: string
  StopDescr: string
  StopDescrEng: string
  StopStreet: string
  StopStreetEng: string | null
  StopHeading: string
  StopLat: string
  StopLng: string
  RouteStopOrder: string
}

export interface OsethBusLocation {
  VEH_NO: string
  CS_DATE: string
  CS_LAT: string
  CS_LNG: string
  ROUTE_CODE: string
}

export interface OsethStopArrival {
  route_code: string
  btime2: string
  veh_code: string
}

interface RouteSummary {
  id: string
  shortName: string
  longName: string
  tripHeadsigns?: Array<{
    routeId: string
    headsign: string
    shapeId: string
  }>
}

interface StopSummary {
  id: string
  code: string
  name: string
  latitude: number
  longitude: number
  sequence?: number
  distance?: number | null
  routes?: RouteSummary[]
}

interface VehicleSummary {
  id: string
  latitude: number
  longitude: number
}

interface TripSummary {
  shapeId: string
  arrivalInMinutes: number
  route: RouteSummary
  vehicle?: VehicleSummary | null
}

interface OsethEnvelope<T> {
  data: T
  error?: string
  status_code?: number
}

type RequestJson = (path: string) => Promise<unknown>

const ROUTE_SEPARATOR = '~'

export function encodeOsethRouteCode(routeId: string, shapeId: string): string {
  return `${routeId}${ROUTE_SEPARATOR}${shapeId}`
}

export function decodeOsethRouteCode(routeCode: string): { routeId: string; shapeId: string } {
  const separatorIndex = routeCode.lastIndexOf(ROUTE_SEPARATOR)
  if (separatorIndex < 1 || separatorIndex === routeCode.length - 1) {
    throw new Error(`Invalid OSETH route code: ${routeCode}`)
  }

  return {
    routeId: routeCode.slice(0, separatorIndex),
    shapeId: routeCode.slice(separatorIndex + 1),
  }
}

function unwrap<T>(payload: unknown): T {
  const envelope = payload as OsethEnvelope<T>
  if (!envelope || typeof envelope !== 'object' || !('data' in envelope)) {
    throw new Error('OSETH returned an invalid response.')
  }
  if (envelope.status_code && envelope.status_code !== 200) {
    throw new Error(envelope.error || `OSETH request failed (${envelope.status_code}).`)
  }
  return envelope.data
}

function routeType(routeId: string): string {
  return routeId.match(/_(1|2)_\d+$/)?.[1] || '1'
}

function mapRouteDirections(route: RouteSummary): OsethRouteInfo[] {
  return (route.tripHeadsigns || []).map(direction => ({
    RouteCode: encodeOsethRouteCode(direction.routeId, direction.shapeId),
    LineCode: route.shortName,
    RouteDescr: direction.headsign,
    RouteDescrEng: direction.headsign,
    RouteType: routeType(direction.routeId),
    RouteDistance: '',
  }))
}

function mapStop(stop: StopSummary, index: number): OsethStopInfo {
  return {
    StopCode: stop.code,
    StopID: stop.id,
    StopDescr: stop.name,
    StopDescrEng: stop.name,
    StopStreet: '',
    StopStreetEng: null,
    StopHeading: '',
    StopLat: String(stop.latitude),
    StopLng: String(stop.longitude),
    RouteStopOrder: String(stop.sequence ?? index + 1),
  }
}

function athensDate(date: Date): string {
  const values = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Athens',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date).reduce<Record<string, string>>((result, part) => {
    result[part.type] = part.value
    return result
  }, {})

  return `${values.day}/${values.month}/${values.year} ${values.hour}:${values.minute}:${values.second}`
}

export function createOsethApi(requestJson: RequestJson, now: () => Date = () => new Date()) {
  const getRouteSummaries = async (shortName?: string): Promise<RouteSummary[]> => {
    const search = shortName ? `&routeShortName=${encodeURIComponent(shortName)}` : ''
    const payload = await requestJson(`/el/telematics-api/route?page=1&size=1000${search}`)
    return unwrap<{ routes: RouteSummary[] }>(payload).routes || []
  }

  const getRoutes = async (lineCode: string): Promise<OsethRouteInfo[]> => {
    const routes = await getRouteSummaries(lineCode)
    return routes
      .filter(route => route.shortName.toUpperCase() === lineCode.toUpperCase())
      .flatMap(mapRouteDirections)
  }

  const getStopInfo = async (stopCode: string): Promise<StopSummary & { trips?: TripSummary[] }> => {
    const payload = await requestJson(
      `/el/telematics-api/stop/${encodeURIComponent(stopCode)}/info?language=el`,
    )
    return unwrap<StopSummary & { trips?: TripSummary[] }>(payload)
  }

  return {
    getLines: async (): Promise<OsethLineInfo[]> => {
      const routes = await getRouteSummaries()
      return routes.map(route => ({
        LineCode: route.shortName,
        LineID: route.shortName,
        LineDescr: route.longName,
        LineDescrEng: route.longName,
      }))
    },

    getRoutes,

    getStops: async (routeCode: string): Promise<OsethStopInfo[]> => {
      const { routeId, shapeId } = decodeOsethRouteCode(routeCode)
      const payload = await requestJson(
        `/el/telematics-api/route/${encodeURIComponent(routeId)}/info?shapeId=${encodeURIComponent(shapeId)}&language=el`,
      )
      const data = unwrap<{ stops?: StopSummary[] }>(payload)
      return (data.stops || []).map(mapStop)
    },

    getBusLocations: async (routeCode: string): Promise<OsethBusLocation[]> => {
      const { routeId, shapeId } = decodeOsethRouteCode(routeCode)
      const payload = await requestJson(
        `/el/telematics-api/route/${encodeURIComponent(routeId)}/info?shapeId=${encodeURIComponent(shapeId)}&language=el`,
      )
      const data = unwrap<{ vehicles?: VehicleSummary[] }>(payload)
      const timestamp = now().toISOString()
      return (data.vehicles || []).map(vehicle => ({
        VEH_NO: vehicle.id,
        CS_DATE: timestamp,
        CS_LAT: String(vehicle.latitude),
        CS_LNG: String(vehicle.longitude),
        ROUTE_CODE: routeCode,
      }))
    },

    getStopArrivals: async (stopCode: string): Promise<OsethStopArrival[]> => {
      const date = encodeURIComponent(athensDate(now()))
      const payload = await requestJson(
        `/el/telematics-api/stop/${encodeURIComponent(stopCode)}/timetable?language=el&date=${date}`,
      )
      const data = unwrap<{ trips?: TripSummary[] }>(payload)
      return (data.trips || []).map(trip => ({
        route_code: encodeOsethRouteCode(trip.route.id, trip.shapeId),
        btime2: String(trip.arrivalInMinutes),
        veh_code: trip.vehicle?.id || '',
      }))
    },

    getStopDetails: async (stopCode: string) => {
      const stop = await getStopInfo(stopCode)
      return {
        stop_code: stop.code,
        stop_descr: stop.name,
        stop_descr_eng: stop.name,
        stop_lat: String(stop.latitude),
        stop_lng: String(stop.longitude),
      }
    },

    getRoutesForStop: async (stopCode: string) => {
      const stop = await getStopInfo(stopCode)
      const routes = stop.routes || []
      const directionsByLine = new Map<string, OsethRouteInfo[]>()
      await Promise.all([...new Set(routes.map(route => route.shortName))].map(async shortName => {
        directionsByLine.set(shortName, await getRoutes(shortName))
      }))

      return routes.flatMap(route => {
        const directions = directionsByLine.get(route.shortName) || []
        const direction = directions.find(item => decodeOsethRouteCode(item.RouteCode).routeId === route.id)
        if (!direction) return []
        return [{
          ...direction,
          LineID: route.shortName,
          LineDescr: route.longName,
          LineDescrEng: route.longName,
        }]
      })
    },

    getClosestStops: async (latitude: number, longitude: number): Promise<OsethStopInfo[]> => {
      const payload = await requestJson(
        `/el/telematics-api/stop/nearby?page=1&size=20&longitude=${longitude}&latitude=${latitude}&language=el`,
      )
      const data = unwrap<{ stops?: StopSummary[] }>(payload)
      return (data.stops || []).map(mapStop)
    },
  }
}
